import { useEffect, useState } from "react";
import { Check, Flame, X } from "lucide-react";
import { toast } from "sonner";
import { api, C, fmtChips } from "../../lib/api";
import { AssetImage } from "../AssetImage";
import { sounds } from "../../lib/sounds";

const Tile = ({ t, state }) => {
  const claimed = t.day <= state.claimed_through;
  const current = t.day === state.day && state.claimable;
  const vip = t.vip > 0;
  return (
    <div data-testid={`streak-day-${t.day}`} data-state={claimed ? "claimed" : current ? "current" : "locked"}
      className={`relative rounded-xl border flex flex-col items-center justify-between py-2 px-1 min-h-[96px] transition-transform
        ${current ? "border-amber-400 bg-amber-400/15 shadow-[0_0_28px_rgba(251,191,36,0.45)] scale-[1.06] z-10" : claimed ? "border-emerald-400/40 bg-emerald-400/5" : vip ? "border-amber-200/30 bg-black/40" : "border-white/10 bg-black/40"}`}>
      <div className={`text-[10px] font-display tracking-wider px-2 rounded-b-md -mt-2 ${vip ? "bg-amber-400 text-slate-900" : current ? "bg-amber-300 text-slate-900" : "bg-slate-700 text-amber-100"}`}>DAY {t.day}</div>
      <div className="flex-1 flex items-center">
        {claimed ? <Check className="w-7 h-7 text-emerald-300" />
          : vip ? <AssetImage id="sng_wait_coin" width={36} className="w-9 h-9 drop-shadow" />
            : <AssetImage id="chip_1000" width={30} className="w-8 h-8 drop-shadow" />}
      </div>
      <div className={`font-mono text-xs ${vip ? "text-amber-300" : "text-amber-50"}`}>{vip ? `${t.vip} VIP` : fmtChips(t.chips)}</div>
    </div>
  );
};

// "The Daily Streak": shown once per day, right after login, when today's reward is waiting.
export const DailyStreakModal = ({ onClose, onClaimed }) => {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [won, setWon] = useState(null);
  useEffect(() => { api.get(C.ApiRoutes.STREAK).then((r) => setState(r.data)).catch(onClose); }, [onClose]);
  if (!state) return null;
  const claim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(C.ApiRoutes.STREAK_CLAIM);
      setWon(data.reward); setState(data); sounds.chipsBet();
      onClaimed?.(data.reward);
      toast.success(data.reward.vip ? `+${data.reward.vip} VIP points banked` : `+${fmtChips(data.reward.chips)} added to your wallet`, { description: `Day ${data.reward.day} of ${C.TDA.DAILY_STREAK_DAYS}${data.reward.grinder_points ? ` · +${data.reward.grinder_points} Weekly Grinder points` : ""} — come back tomorrow for more.` });
    } catch { toast.error("Already claimed today"); } finally { setBusy(false); }
  };
  const todays = state.ladder[state.day - 1];
  return (
    <div data-testid="daily-streak" className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="gold-card noise rounded-3xl w-full max-w-3xl text-slate-100 rise overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.5em] text-amber-300/80">Players Room</div>
            <div className="font-display text-2xl md:text-3xl text-amber-100 tracking-wide flex items-center gap-2"><Flame className="w-6 h-6 text-amber-400" /> THE DAILY STREAK</div>
          </div>
          <div className="flex items-center gap-3">
            <div data-testid="streak-day-badge" className="rounded-full bg-amber-400 text-slate-900 font-display text-sm px-4 py-1">DAY {state.day}</div>
            <button data-testid="streak-close" onClick={onClose} className="rounded-full p-1.5 hover:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="px-6 pt-4 text-sm text-slate-300">Log in every day and the reward grows — <span className="text-amber-200">a little more every day</span>, VIP points every 7th day. Miss a day and the streak starts over.</div>
        <div className="grid grid-cols-7 gap-2 p-6">{state.ladder.map((t) => <Tile key={t.day} t={t} state={state} />)}</div>
        <div className="flex items-center justify-between gap-4 px-6 pb-6">
          <div className="text-xs text-slate-400">{won ? `Claimed — see you tomorrow for day ${state.day % C.TDA.DAILY_STREAK_DAYS + 1}.` : "Today's reward is waiting."}</div>
          {won ? (
            <button data-testid="streak-done" onClick={onClose} className="rounded-full px-8 py-3 font-display tracking-wider bg-white/10 hover:bg-white/20">PLAY</button>
          ) : (
            <button data-testid="streak-claim" disabled={busy || !state.claimable} onClick={claim}
              className="rounded-full px-10 py-3 font-display tracking-wider text-lg btn-gold btn-lift disabled:opacity-40 shadow-[0_0_30px_rgba(251,191,36,0.5)]">
              CLAIM {todays.vip ? `${todays.vip} VIP` : fmtChips(todays.chips)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
